import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import useIsMobile from "../hooks/useIsMobile";
import DropdownMenu from "../common/DropdownMenu";
import { AvailableStatusOptions, StageValue } from "./workflowStates";

export default ({
  currentStage,
  accountType,
  disabled,
  dropdownButtonId,
  onClick,
  withComment,
}: {
  currentStage: StageValue;
  disabled: boolean;
  accountType: "advisor" | "member";
  dropdownButtonId?: string;
  onClick: (value: StageValue, holdDate?: string) => void;
  withComment: boolean;
}) => {
  const isMobile = useIsMobile();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [holdDate, setHoldDate] = useState<Date | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<StageValue | null>(null);

  // Status options for the dropdown button
  const statusOptions = AvailableStatusOptions(
    currentStage || "new",
    accountType,
    withComment,
  );

  const allowStageDate = (value: StageValue) => {
    return value === StageValue.OnHold;
  };

  const getStatusColor = (variant: string) => {
    switch (variant) {
      case "teal":
        return "#198282";
      case "orange":
      case "warning":
        return "#FF9800";
      case "red":
        return "#D32F2F";
      case "green":
        return "#4CAF50";
      case "gray":
      default:
        return "#8E9598";
    }
  };

  const handleMenuItemClick = (value: StageValue) => {
    // If the user selects the same status that's already selected, reset to initial state
    if (selectedStatus === value) {
      setSelectedStatus(null);
      setShowDatePicker(false);
      setHoldDate(null);
    } else {
      setSelectedStatus(value);
      setShowDatePicker(allowStageDate(value));
    }
    // Call onClick to notify parent of status selection (without saving)
    onClick(value);
  };

  const getButtonText = () => {
    if (selectedStatus) {
      // If the selected status is the same as the current stage, show "Update status"
      if (selectedStatus === currentStage) {
        return "Update status";
      }
      const selectedOption = statusOptions.find(
        (option) => option.value === selectedStatus,
      );
      return selectedOption ? selectedOption.label : "Update status";
    }
    return "Update status";
  };

  const getSelectedStatusColor = () => {
    if (!selectedStatus || selectedStatus === currentStage) {
      return null;
    }
    const selectedOption = statusOptions.find(
      (option) => option.value === selectedStatus,
    );
    return selectedOption ? getStatusColor(selectedOption.variant) : null;
  };

  const hasSelectedStatus = !!selectedStatus && selectedStatus !== currentStage;

  // Convert statusOptions to DropdownMenu options format
  const dropdownOptions = statusOptions.map((option) => ({
    key: option.value,
    title: (
      <>
        <Typography variant="body">{option.label}</Typography>
        {option.value === currentStage && (
          <Typography variant="bodySmall" color="text.tertiary">
            {hasSelectedStatus ? "(Reset)" : "(Current)"}
          </Typography>
        )}
      </>
    ),
    startIcon: (
      <Box
        sx={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          backgroundColor: getStatusColor(option.variant),
        }}
      />
    ),
    onClick: () => handleMenuItemClick(option.value),
  }));

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "start",
        gap: "12px",
        flexDirection: isMobile ? "column" : "row",
      }}
    >
      {showDatePicker && (
        <DatePicker
          value={holdDate}
          onChange={(newDate) => setHoldDate(newDate)}
          minDate={new Date()}
          sx={{
            ".MuiInputBase-input": {
              marginTop: "0",
            },
            ".MuiInputBase-root": {
              minHeight: "0",
            },
            "& .MuiInputBase-root": {
              height: "40px",
              fontSize: "12px",
              padding: "0 20px 0 8px",
            },
            "& .MuiInputBase-input": {
              padding: "6px 8px",
            },
          }}
          slotProps={{
            textField: {
              size: "small",
              placeholder: "Hold date (optional)",
              variant: "outlined",
            },
          }}
        />
      )}
      <DropdownMenu
        id={dropdownButtonId}
        disabled={disabled}
        variant="outlinedSecondary"
        size="small"
        dropdownIcon={true}
        buttonContent={
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {getSelectedStatusColor() && (
              <Box
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: getSelectedStatusColor(),
                }}
              />
            )}
            {getButtonText()}
          </Box>
        }
        options={dropdownOptions}
        sx={{
          minWidth: "140px",
          justifyContent: "space-between",
          color: hasSelectedStatus ? "text.primary" : "text.secondary",
          fontWeight: "500",
        }}
      />
    </Box>
  );
};
